import { useEffect, useState, useCallback } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import store from 'store'
import { useFormik } from 'formik'
import * as Yup from 'yup'

import { categoryService, mediaService, productService } from 'services'
import { brandsService } from 'services/brand.service'

import { _object } from 'utils/interfaces'

import ReactQuill from 'react-quill'
import { toast } from 'react-toastify'
import { Button, InputField } from 'components/form-inputs'
import { DeleteIcon } from 'assets/svg'

const EditProduct = () => {
  const { id } = useParams()
  const navigate = useNavigate()

  const [product, setProduct] = useState<_object>({})
  const [brands, setBrands] = useState<_object>({})
  // const [countries, setCountries] = useState<_object>({})
  const [categories, setCategories] = useState<_object>({})
  // const [taxRates, setTaxRates] = useState<_object>({})
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [selectedPreview, setSelectedPreview] = useState<string | null>(null)
  const [isUploading, setIsUploading] = useState(false)
  const [loading, setLoading] = useState(false)
  const [loadingId, setLoadingId] = useState<string | null>(null)

  const getProductDetails = useCallback(() => {
    productService.getProductDetails(id).then((results: _object) => {
      setProduct(results)
    })
  }, [id])

  useEffect(() => {
    getProductDetails()
  }, [getProductDetails])

  useEffect(() => {
    brandsService.getBrands({ per_page: 999 }).then((results: _object) => {
      setBrands(results)
    })
    // countryService.all().then((results: _object) => {
    //   setCountries(results)
    // })
    categoryService.getCategories({ per_page: 999 }).then((results: _object) => {
      setCategories(results)
    })
    // taxService.all().then((results: _object) => {
    //   setTaxRates(results)
    // })

  }, [])

  const validationSchema = Yup.object().shape({
    name: Yup.string().required('Product name is required'),
    regular_price: Yup.number().required('Regular price is required'),
    brand_id: Yup.string().required('Brand is required'),
    category_id: Yup.string().required('Category is required'),
    country: Yup.string().required('Country is required')
  })

  const formik: any = useFormik({
    initialValues: {
      name: product?.name || '',
      barcode: product?.barcode || '',
      description: product?.description || '',
      short_description: product?.short_description || '',
      regular_price: product?.regular_price ?? '',
      discounted_price: product?.discounted_price ?? '',
      manage_stock: false,
      // low_stock_threshold: product?.low_stock_threshold ?? '',
      stock_status: product?.stock_status || 'instock',
      status: product?.status || 'published',
      // tax_status: product?.tax_status ?? true,
      featured: product?.featured ?? false,
      // weight: product?.weight ?? '',
      // length: product?.length ?? '',
      // width: product?.width ?? '',
      // height: product?.height ?? '',
      // tax_rate_id: product?.tax_rate_id || '',
      brand_id: product?.brand_id || '',
      category_id: product?.category_id || '',
      country: product?.country?.name || 'Australia',
      units_per_carton: product?.units_per_carton || null
    },
    enableReinitialize: true,
    validateOnMount: true,
    validationSchema,
    onSubmit: async (values) => {
      setLoading(true)
      if (!id) {
        const results: _object = await productService.createProduct(values)
        if (results.error === false) {
          const newId = results?.data?.id
          if (selectedFile && newId) {
            await uploadImage(newId)
          }
          navigate('/app/products')
        }
        setLoading(false)
      } else {
        productService.updateProduct(id, values).then((results: _object) => {
          if (results.error === false) {
            navigate('/app/products')
          }
          setLoading(false)
        })
      }
    }
  })

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files ? e.target.files[0] : null
    if (file) {
      const url = URL.createObjectURL(file)
      setSelectedPreview(url)
      setSelectedFile(file)
    }
  }

  const uploadImage = useCallback(async (entityId?: string) => {
    if (!selectedFile) return null
    setIsUploading(true)
    const data = new FormData()
    if (entityId) {
      data.append('entity_id', entityId)
    } else if (id) {
      data.append('entity_id', id)
    }
    data.append('entity_type', 'product')
    data.append('key', 'product_image')
    data.append('files', selectedFile)
    const accessToken = store.get(process.env.REACT_APP_ACCESS_TOKEN_KEY || '')

    try {
      const response = await fetch(`${process.env.REACT_APP_BASE_URL}/media`, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          Authorization: accessToken ? accessToken : ''
        },
        body: data
      })

      const result = await response.json()
      if (result.error === false) {
        getProductDetails()
        toast.success(result.message || 'Image uploaded successfully.', {
          autoClose: 3000
        })
        if (selectedPreview) {
          try {
            URL.revokeObjectURL(selectedPreview)
          } catch (e) {
            console.log(e);

          }
        }
        setSelectedPreview(null)
        setSelectedFile(null)
      } else if (result.error === true) {
        toast.error(result.message || 'Image not uploaded.', {
          autoClose: 3000
        })
      }
      return result
    } finally {
      setIsUploading(false)
      getProductDetails()
    }
  }, [id, selectedFile, selectedPreview, getProductDetails])

  useEffect(() => {
    if (!selectedFile) return
    if (!id) return
    uploadImage()
  }, [selectedFile, id, uploadImage])

  useEffect(() => {
    return () => {
      if (selectedPreview) {
        URL.revokeObjectURL(selectedPreview)
      }
    }
  }, [selectedPreview])

  const [selectedId, setSelectedId] = useState<string | null>(null)
  const featuredImage = (id: string) => {
    setLoadingId(id)
    setSelectedId(id)
    mediaService.upDateMediaData(id, {
      sequence: 1
    })
      .then((results: _object) => {
        console.log('results', results)
      })
      .finally(() => {
        setLoadingId(null)
        getProductDetails()
      })
  }

  const handleDeleteProduct = (id: string) => {
    const confirmed = window.confirm('Are you sure you want to delete ?')
    if (!confirmed) return

    mediaService.deleteMediaData(id).then((results: _object) => {
      if (results.error === false) {
        getProductDetails()
      }
    })
  }

  return (
    <>
      <div className="mb-6">
        <h1 className="mb-1 text-2xl font-semibold">
          {product?.name ? 'Edit Product' : 'Add Product'}
        </h1>
      </div>
      <div className="">
        <div className="">
          <form onSubmit={formik.handleSubmit}>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              <div className="mb-4">
                <InputField
                  label='Product name'
                  placeholder='Product name'
                  name='name'
                  onChange={formik.handleChange}
                  value={formik.values.name}
                  required
                  error={formik.touched.name && formik.errors.name}
                />
              </div>



              <div className="mb-4">
                <InputField
                  label='Regular Price'
                  type='number'
                  placeholder='Regular Price'
                  name='regular_price'
                  onChange={formik.handleChange}
                  value={formik.values.regular_price}
                  required
                  error={formik.touched.regular_price && formik.errors.regular_price}
                />
              </div>

              <div className="mb-4">
                <InputField
                  label='Discounted Price'
                  type='number'
                  placeholder='Discounted Price'
                  name='discounted_price'
                  onChange={formik.handleChange}
                  value={formik.values.discounted_price}
                />
              </div>
              <div className="mb-4">
                <InputField
                  label='Barcode'
                  placeholder='Barcode'
                  name='barcode'
                  onChange={formik.handleChange}
                  value={formik.values.barcode}
                />
              </div>
              <div className="mb-4">
                <label htmlFor="brand" className=" block text-sm font-medium text-gray-900">Brand<span className="text-red-500">*</span> </label>
                <select
                  id="brand"
                  name="brand_id"
                  value={formik.values.brand_id}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  className="block w-full appearance-none rounded-md border bg-gray-100   px-3 py-2 pr-10 text-sm outline-none focus:outline-none"
                  required
                >
                  <option value="">Select a brand</option>
                  {brands?.items?.map((item: _object, i: number) => (
                    <option key={i} value={item.id}>
                      {item.title}
                    </option>
                  ))}
                </select>
                {formik.touched.brand_id && formik.errors.brand_id && (
                  <p className="mt-1 text-xs text-red-500">{formik.errors.brand_id as string}</p>
                )}
              </div>

              <div className="mb-4">
                <label htmlFor="category" className="block text-sm font-medium text-gray-900">Category <span className="text-red-500">*</span></label>
                <select
                  id="category"
                  name="category_id"
                  value={formik.values.category_id}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  className="block w-full appearance-none rounded-md border bg-gray-100 px-3 py-2 pr-10 text-sm outline-none focus:outline-none"
                  required
                >
                  <option value="">Select a category</option>
                  {categories?.items?.map((item: _object, i: number) => (
                    <option key={i} value={item.id}>
                      {item.name}
                    </option>
                  ))}
                </select>
                {formik.touched.category_id && formik.errors.category_id && (
                  <p className="mt-1 text-xs text-red-500">{formik.errors.category_id as string}</p>
                )}
              </div>
              <div className="mb-4">
                <label htmlFor="country" className="block text-sm font-medium text-gray-900">Country <span className="text-red-500">*</span></label>
                <select
                  id="country"
                  name="country"
                  value={formik.values.country}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  className="block w-full appearance-none rounded-md border bg-gray-100 px-3 py-2 pr-10 text-sm outline-none focus:outline-none"
                  required
                >
                  <option value="">Select a country</option>
                  {[
                    "India",
                    "United States",
                    "United Kingdom",
                    "Canada",
                    "Australia",
                    "Germany",
                    "France",
                    "Italy",
                    "Spain",
                    "Brazil",
                    "China",
                    "Japan",
                    "Russia",
                    "South Africa",
                    "Mexico",
                    "Indonesia",
                    "Saudi Arabia",
                    "Argentina"
                  ].map((item: string, i: number) => (
                    <option key={i} value={item}>
                      {item}
                    </option>
                  ))}
                </select>
                {formik.touched.country && formik.errors.country && (
                  <p className="mt-1 text-xs text-red-500">{formik.errors.country as string}</p>
                )}
              </div>
              <div className="mb-4">
                <InputField
                  label='units per carton'
                  type='number'
                  placeholder='units per carton'
                  name='units_per_carton'
                  onChange={formik.handleChange}
                  value={formik.values.units_per_carton}
                  error={formik.touched.units_per_carton && formik.errors.units_per_carton}
                />
              </div>
              <div className="mb-4">
                <label className="mb-2 block text-sm font-medium text-gray-900">Status </label>
                <div className="flex items-center gap-4 text-sm">
                  <label className="flex items-center gap-2">
                    <input
                      type="radio"
                      name="status"
                      value="published"
                      checked={formik.values.status === 'published'}
                      onChange={() => formik.setFieldValue('status', 'published')}
                    />
                    Published
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="radio"
                      name="status"
                      value="draft"
                      checked={formik.values.status === 'draft'}
                      onChange={() => formik.setFieldValue('status', 'draft')}
                    />
                    Draft
                  </label>
                </div>
              </div>

              {/* <div className="mb-4">
                <label className="mb-2 block text-sm font-medium text-gray-900">Tax Status <span className="text-red-500">*</span></label>
                <div className="flex items-center gap-4 text-sm">
                  <label className="flex items-center gap-2">
                    <input
                      type="radio"
                      name="tax_status"
                      value="true"
                      checked={formik.values.tax_status === true || formik.values.tax_status === 'true'}
                      onChange={() => formik.setFieldValue('tax_status', true)}
                    />
                    Taxable
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="radio"
                      name="tax_status"
                      value="false"
                      checked={formik.values.tax_status === false || formik.values.tax_status === 'false'}
                      onChange={() => formik.setFieldValue('tax_status', false)}
                    />
                    Non Taxable
                  </label>
                </div>
              </div> */}

              <div className="mb-4">
                <label className="mb-2 block text-sm font-medium text-gray-900">Featured </label>
                <div className="flex items-center gap-4 text-sm">
                  <label className="flex items-center gap-2">
                    <input
                      type="radio"
                      name="featured"
                      value="true"
                      checked={formik.values.featured === true || formik.values.featured === 'true'}
                      onChange={() => formik.setFieldValue('featured', true)}
                    />
                    Yes
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="radio"
                      name="featured"
                      value="false"
                      checked={formik.values.featured === false || formik.values.featured === 'false'}
                      onChange={() => formik.setFieldValue('featured', false)}
                    />
                    No
                  </label>
                </div>
              </div>

              <div className="mb-4">
                <label className="mb-2 block text-sm font-medium text-gray-900">Stock Status </label>
                <div className="flex items-center gap-4 text-sm">
                  <label className="flex items-center gap-2">
                    <input
                      type="radio"
                      name="stock_status"
                      value="instock"
                      checked={formik.values.stock_status === 'instock'}
                      onChange={() => formik.setFieldValue('stock_status', 'instock')}
                    />
                    In Stock
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="radio"
                      name="stock_status"
                      value="outofstock"
                      checked={formik.values.stock_status === 'outofstock'}
                      onChange={() => formik.setFieldValue('stock_status', 'outofstock')}
                    />
                    Out of Stock
                  </label>
                </div>
              </div>


              <div className="mb-4 col-span-1 sm:col-span-2 lg:col-span-3">
                <label htmlFor="short_description" className="mb-2 block text-sm font-medium text-gray-900">Product Information </label>
                <ReactQuill
                  id="short_description"
                  value={formik.values.short_description}
                  onChange={(value: string) => formik.setFieldValue('short_description', value || '')}
                  className="w-full text-sm outline-none focus:outline-none"
                  placeholder="Product Information"
                />
              </div>
              <div className="mb-4 col-span-1 sm:col-span-2 lg:col-span-3">
                <label htmlFor="description" className="mb-2 block text-sm font-medium text-gray-900">Product Description </label>
                <ReactQuill
                  id="description"
                  value={formik.values.description}
                  onChange={(value: string) => formik.setFieldValue('description', value || '')}
                  className="w-full text-sm outline-none focus:outline-none"
                  placeholder="Product Description"
                />
              </div>

              <div className="p-5 col-span-1 sm:col-span-2 lg:col-span-3">
                <h3 className="mb-1 text-sm font-medium text-gray-900">Product Images </h3>
                <h3 className="mb-2 text-[10px] text-[#969494] font-medium">221W × 221H | 1:1 </h3>
                <div className="flex flex-wrap gap-4">

                  {selectedPreview && (
                    <div className="relative w-32 h-32 cursor-pointer" key="preview">
                      <div className="absolute top-2 right-2 z-20">
                        <button
                          type="button"
                          onClick={() => {
                            if (selectedPreview) URL.revokeObjectURL(selectedPreview)
                            setSelectedPreview(null)
                            setSelectedFile(null)
                          }}
                          className="flex items-center justify-center w-8 h-8 rounded-full bg-white/90 border border-gray-200 shadow"
                          title="Remove preview"
                        >
                          <DeleteIcon className="h-4 w-4 text-red-500" />
                        </button>
                      </div>

                      <div className="flex h-full w-full items-center justify-center rounded-md bg-gray-100 shadow relative">
                        <img src={selectedPreview} alt="preview" className="h-24 w-24 object-contain" />
                        {isUploading && (
                          <div className="absolute inset-0 flex items-center justify-center bg-white/60 rounded-md z-20">
                            <svg
                              aria-hidden="true"
                              className="w-6 h-6 text-gray-300 animate-spin fill-blue-600"
                              viewBox="0 0 100 101"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            ></svg>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {product?.media?.map((item: _object, i: number) => {
                    const showStar = selectedId === item.id || (selectedId === null && i === 0);

                    return (
                      <div
                        className="relative w-32 h-32 cursor-pointer group"
                        key={i}
                      >
                        <div className="absolute top-2 right-2 z-20 flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => handleDeleteProduct(item.id)}
                            className="flex items-center justify-center w-8 h-8 rounded-full bg-white/90 border border-gray-200 shadow"
                            title="Delete this media"
                          >
                            <DeleteIcon className="h-4 w-4 text-red-500" />
                          </button>

                          {!showStar && (
                            <button
                              type="button"
                              onClick={() => featuredImage(item.id)}
                              className="flex items-center justify-center w-8 h-8 rounded-full bg-white/90 border border-gray-200 shadow"
                              title="Select this media"
                            >
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                version="1.1"
                                width="16"
                                height="16"
                                viewBox="0 0 512 512"
                              >
                                <path
                                  fill="#581c87"
                                  d="m3 187-1-1h179v-1h1l10-24L256 2v1-1l64 159 10 24h180s-1 1-1 2c-16 17-151 115-151 115l59 185h-5l-7-3c-29-16-143-84-143-84l-162 87h-5l59-185S18 204 3 187zm394 272-87-173 121-72-138 8-38-119-38 119-138-8 121 72-80 168 142-104 134 109z"
                                />
                              </svg>
                            </button>
                          )}
                        </div>

                        {showStar && (
                          <span className="absolute top-0 right-0 text-purple-900 z-30 pr-1">
                            <svg xmlns="http://www.w3.org/2000/svg" version="1.1" width="18" height="24" x="0" y="0" viewBox="0 0 512 512"><g><path d="m512 197.816-186.039-12.231L255.898 9.569l-70.063 176.016L0 197.816l142.534 121.026-46.772 183.589L255.898 401.21l160.137 101.221-46.772-183.589z" fill="#581c87" opacity="1" data-original="#000000"></path></g></svg>
                          </span>
                        )}

                        <div
                          className="flex h-full w-full items-center justify-center rounded-md bg-gray-100 shadow relative"
                        >
                          <img
                            src={item?.path}
                            alt={`shirt${i + 1}`}
                            className="h-24 w-24 object-contain"
                          />

                          {loadingId === item.id && (
                            <div className="absolute inset-0 flex items-center justify-center bg-white/60 rounded-md z-20">
                              <svg
                                aria-hidden="true"
                                className="w-6 h-6 text-gray-300 animate-spin fill-blue-600"
                                viewBox="0 0 100 101"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                              </svg>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}

                  <label htmlFor="imageUpload">
                    <div className="flex h-32 w-32 cursor-pointer items-center justify-center rounded-md border-2 border-dashed border-purple-900">
                      <input
                        type="file"
                        id="imageUpload"
                        accept=".png, .jpg, .jpeg"
                        onChange={handleImageChange}
                        className="hidden"
                      />
                      {isUploading ? (
                        <svg
                          className="h-8 w-8 animate-spin text-purple-900"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                          ></path>
                        </svg>
                      ) : (
                        <span className="text-4xl font-bold text-purple-900">+</span>
                      )}
                    </div>
                  </label>

                </div>
              </div>

              <div className="col-span-1 sm:col-span-2 lg:col-span-3 flex justify-center">
                <Button
                  label="Save changes"
                  type="submit"
                  className="mt-4 inline-flex justify-center rounded-lg bg-purple-900 px-6 py-2 text-sm text-white transition hover:bg-purple-950 disabled:opacity-50"
                  loading={loading}
                  disabled={!formik.isValid || !formik.dirty}
                />
              </div>
            </div>
          </form>
        </div>
      </div>
    </>
  )
}

export default EditProduct
